import { ImSys } from '../core/ImSys.js';
import { ImViewBridge } from '../legacy/ImViewBridge.js';
import { Widget } from '../core/Widget.js';
import { ConfigTools } from './ConfigTools.js';
import { ViewMode } from './ViewOptions.js';
import { ViewCmdType } from './IViewWidget.js';
import { TabsPanelWidget, TABS_PANEL_WIDGET_KEY } from '../widgets/TabsPanelWidget.js';
import { UiCommandRegistry } from '../core/imcmd/UiCommandRegistry.js';
import { UiCommandRunner } from '../core/imcmd/UiCommandRunner.js';
import { ViewServerCmdProvider } from './cmd/ViewServerCmdProvider.js';
import { ViewUiCommandProvider } from './cmd/ViewUiCommandProvider.js';
import { CmdPanelWidget, CmdBus } from '../widgets/CmdPanelWidget.js';
import { ViewBusyController } from './controllers/ViewBusyController.js';
import { ViewServerExecutor } from './transport/ViewServerExecutor.js';
import { ServerExecutor } from './transport/ServerExecutor.js';
import { ViewDomController } from './controllers/ViewDomController.js';
import { ViewValidationController } from './controllers/ViewValidationController.js';
import { Loc } from '../locales/registry.js';
import { PageCloseController } from '../core/PageCloseController.js';
import { TableWidgetHelper } from '../widgets/table/TableWidgetHelper.js';
import { DomHelpers } from '../core/DomHelpers.js';
import { requestAsideClose, hasUnsavedAsideChanges, confirmAsideClose } from '../core/tools/ModalAside.js';
import { BgSaveScope } from './BgSaveScope.js';
import { encodeValues } from '../core/EncodeValues.js';
import { Validation } from '../core/Validation.js';
import { on, off } from '../core/Events.js';
import { ApplyButtonController } from '../search-core/ApplyButtonController.js';
import { ViewUiCommand } from './cmd/ViewUiCommand.js';

class ViewWidget extends Widget {
    constructor() {
        super(...arguments);
        /**
         * 2026-08-20: признак несохранённых изменений view.
         * Ставится в onValueChanged, снимается на сохранении, вставке и отмене.
         * Сам по себе ни на что не влияет: страж закрытия страницы включается
         * только для view с маркером data-close-control (см. canClose).
         */
        this._closeDirty = false;
        /**
         * 2026-10-15: изменения полей без bgSave и без серверной onchange-команды.
         * Их не сохраняет очередь области фонового сохранения, поэтому закрытие
         * aside с такой областью по-прежнему требует подтверждения (см. ModalAside).
         */
        this._uncoveredDirty = false;
    }
    /** Ключевой класс для поиска виджета */
    static _cssKey() {
        return 'im-view';
    }
    /** Опциональная схема парсинга для этого виджета */
    static _optionScheme() {
        // Подключает декодер конфигурации из data-values
        return { records: ['values'] };
    }
    /** Текущие опции view */
    get options() {
        return this._options;
    }
    /**
     *  Числовой идентификатор view для временных классов
     * (просто по инкременту проставляется)
     */
    get ui() {
        return this._ui;
    }
    ;
    /** Получение маски, ограничивающей контент view. */
    getUiNotMask() {
        return this._uiNotMask;
    }
    /** Контроллер dom элементов */
    get dom() {
        return this._domController;
    }
    ;
    /** Контроллер валидации */
    get validation() {
        return this._validator;
    }
    ;
    /** Командная панель виджета  */
    get cmdPanel() {
        return this._cmdPanel;
    }
    _create(op) {
        this._ui = ImSys.tmp_id++;
        this._uiNotMask = ':not(._x' + this._ui + ' *):not(.im-table tbody *)';
        // Ключевые контроллеры view
        this._domController = new ViewDomController(this);
        this._domController.setXClass();
        this._busyController = new ViewBusyController(this);
        const transport = new ServerExecutor();
        this._executor = new ViewServerExecutor(this, transport);
        this._validator = new ViewValidationController(this);
        let runtime = {};
        // Инициализируем с this._viewCfg
        {
            // Отдаем приоритет data-cfg
            let vc = this.element.dataset.cfg;
            // Поддержка imView, оно уже могло убрать атрибут
            if (!vc) {
                vc = ConfigTools.encodeDataCfg(op);
                // Правильно сформированная конфигурация обязательна
                if (!vc)
                    throw new Error("Конфигурация view не определена");
            }
            runtime.viewCfg = vc;
            runtime.viewKey = ConfigTools.extractViewKey(vc) || '';
        }
        // Установка атрибута data-key
        if (op.dataKey) {
            this.element.dataset.key = op.dataKey;
        }
        runtime.dataKey = this.element.dataset.key;
        // Определяем values для запросов ко view
        {
            if (op.values) {
                runtime.values = op.values;
            }
            else {
                const valStr = this.element.dataset.values;
                if (valStr) {
                    runtime.values = ConfigTools.decodeRecordString(valStr);
                }
            }
        }
        // Обрабатываем вариант
        {
            if (op.variant) {
                // В legacy-режиме оставляем атрибут в DOM
                if (ImSys.legacyJqView) {
                    this.element.dataset.variant = op.variant;
                }
                runtime.variant = op.variant;
            }
            else {
                const variantAttr = this.element.dataset.variant;
                if (variantAttr) {
                    runtime.variant = variantAttr;
                }
            }
        }
        // Обычные dom атрибуты
        {
            // Проверка обязательного класса
            if (!this.element.classList.contains("im-view")) {
                this.element.classList.add("im-view");
            }
            // Добавление опциональных классов
            if (op.css) {
                for (const cls of op.css.split(/\s+/).filter(Boolean)) {
                    this.element.classList.add(cls);
                }
            }
            // Стили
            if (op.style) {
                const cur = this.element.getAttribute('style');
                this.element.setAttribute('style', cur ? `${cur};${op.style}` : op.style);
            }
            // id
            if (op.id && this.element.id !== op.id) {
                this.element.id = op.id;
            }
        }
        // Устанавливаем внутренний HTML, если передан
        if (op.innerHTML) {
            this.element.innerHTML = op.innerHTML;
        }
        // На данном этапе завершается формирование runtime
        this._options = runtime;
        // Помещение view в панель вкладок
        if (op.panel || this.element.dataset.panel !== undefined) {
            const panelHost = document.createElement("div");
            panelHost.className = "im-panel";
            this.element.parentElement?.insertBefore(panelHost, this.element);
            const tabs = TabsPanelWidget.getOrInit(panelHost);
            tabs.addTab({
                nodeKey: runtime.dataKey,
                title: op.panel || '',
                element: this.element,
                description: "",
                protect: true,
                viewChanges: false,
            });
        }
        // Инициализация imView в режиме поддержки
        if (ImSys.legacyJqView) {
            if (op.mode)
                ImViewBridge.initImView(this.element, { mode: op.mode, ui: this._ui, uiNotMask: this._uiNotMask, widget_ts: this });
            else
                ImViewBridge.initImView(this.element, { ui: this._ui, uiNotMask: this._uiNotMask, widget_ts: this });
        }
        // Атрибуты удаляемые в режиме без поддержки imView
        else {
            delete this.element.dataset.variant;
            delete this.element.dataset.values;
            delete this.element.dataset.cfg;
            delete this.element.dataset.panel;
        }
        // Инициализация первичного состояния view
        {
            const dk = runtime.dataKey;
            // TODO: В дальнейшем перенести в dom контроллер
            const ins = this._domController.querySelector(`input[data-key=_fv_${dk}_insertMode]`);
            const edt = this._domController.querySelector(`input[data-key=_fv_${dk}_editMode]`);
            if (op.mode) {
                this.setMode(op.mode);
            }
            else if (ins?.value === "True") {
                this.setMode(ViewMode.insert);
            }
            else if (edt?.value === "True") {
                this.setMode(ViewMode.edit);
            }
            else {
                this.setMode(ViewMode.view);
            }
        }
        // Инициализация комманд
        this._ensureCmdBus();
        // Инициализация командной панели
        this._initCmdPanelForView();
        // Инициализация общих событий на уровне view
        this._initEvents();
        // Инициализация html контента
        this.initHtml();
        // Обработка отложенной загрузки контента view
        {
            const hasOnLoading = op.deferredLoad ?? this.element.classList.contains('onloading');
            if (hasOnLoading) {
                if (!this.element.classList.contains('onloading')) {
                    this.element.classList.add('onloading');
                }
                setTimeout(() => {
                    this.reload();
                });
            }
        }
    }
    /** Инициализация панели кнопок */
    _initCmdPanelForView() {
        // host панели внутри текущего view
        const host = this._domController.querySelector('.im-view-controls');
        if (!host)
            return; // панель не предусмотрена
        let noSaveClose = false;
        if (!Widget.closestOnByName(this.element, TABS_PANEL_WIDGET_KEY)?.element || this.options.nosaveclose) {
            noSaveClose = true;
        }
        // 4) инициализация CmdPanelWidget
        host.classList.add('im-toolstrip');
        const allows = this.element.getAttribute('data-allow') || "";
        this._cmdPanel = CmdPanelWidget.getOrInit(host, {
            allow: allows,
            filterButton: false,
            pagination: false,
            cmdBus: this._cmdBus,
            addMissingButtons: true,
            noCancelOnInsert: true,
            noSaveClose: noSaveClose
        });
        // 4.1 loading-container в тулстрипе (как было)
        const toolstrip = host; // уже .im-toolstrip
        const hasAnyLoading = this._domController.querySelector('.loading-container:not(.im-table .loading-container)') != null;
        const loadingOff = this.element.getAttribute('data-loadingcontainer') === 'off';
        if (!loadingOff && !hasAnyLoading && !toolstrip.querySelector('.loading-container')) {
            toolstrip.insertAdjacentHTML("beforeend", `<div class='loading-container'><span class='loading ui-corner-all'>${Loc.dataLoading}</span></div>`);
        }
        this.updatePanelState();
        // 5) Гидрация кнопок формы на ту же шину
        this._wireInternalFormButtonsToBus();
    }
    /**
     * Дополняет обязательные значения данными из JSON-объекта или legacy StructuredText.
     * @param valStr Строковое представление обязательных значений
     */
    setRequiredValues(valStr) {
        const decoded = ConfigTools.decodeRecordString(valStr);
        this._options.values ??= {};
        Object.assign(this._options.values, decoded);
    }
    /** Гарантирует наличие локальной шины команд и базовых подписок. */
    _ensureCmdBus() {
        if (this._cmdBus)
            return;
        // host панели внутри текущего view
        const host = this._domController.querySelector('.im-view-controls') || this.element;
        // 1) локальная шина команд
        this._cmdBus = new CmdBus();
        const srcFallback = host;
        // 2) подписки (минимум как в легаси)
        this._cmdBus.on("reload", () => {
            this.reload(null, () => { this.dom.applyViewMode(); }, () => { console.error('err reload'); this.dom.applyViewMode(); }, ViewCmdType.reload);
        });
        this._cmdBus.on("refresh", () => this.reload(null, () => { this.dom.applyViewMode(); }, () => { console.error('err refresh'); this.dom.applyViewMode(); }, ViewCmdType.refresh));
        this._cmdBus.on("edit", (e) => this._startEditFromButton(e?.source || srcFallback));
        this._cmdBus.on("save", (e) => this._saveView(e?.source || srcFallback, false));
        this._cmdBus.on("saveClose", (e) => this._saveView(e?.source || srcFallback, true));
        this._cmdBus.on("cancel", () => this._cancelView());
        this._cmdBus.on("delMark", (e) => {
            const source = e?.source || srcFallback;
            const values = this.encodeValues();
            const request = { type: "delmarkview", cmd: "", values, options: {}, dataKey: this.dataKey, itemKey: source.getAttribute('data-key') ?? undefined };
            this.doJson(request);
        }, { text: Loc.confirmDeleting });
        this._cmdBus.on("delete", (e) => {
            const source = e?.source || srcFallback;
            const values = this.encodeValues();
            const request = { type: "deleteview", cmd: "", values, options: {}, dataKey: this.dataKey, itemKey: source.getAttribute('data-key') ?? undefined };
            this.doJson(request);
        }, { text: Loc.confirmDeleting });
        this._cmdBus.on("rowsMoveUp", (e) => {
            const source = e?.source || srcFallback;
            const values = this.encodeValues();
            const request = { type: "moveup", cmd: "", values, options: {}, dataKey: this.dataKey, itemKey: source.getAttribute('data-key') ?? undefined };
            this.doJson(request, undefined, undefined, true);
        });
        this._cmdBus.on("rowsMoveDown", (e) => {
            const source = e?.source || srcFallback;
            const values = this.encodeValues();
            const request = { type: "movedown", cmd: "", values, options: {}, dataKey: this.dataKey, itemKey: source.getAttribute('data-key') ?? undefined };
            this.doJson(request, undefined, undefined, true);
        });
    }
    /** Начать редактирование (из кнопки) */
    _startEditFromButton(source) {
        const scheme = source.getAttribute('data-scheme') || "";
        const dk = this._options.dataKey;
        const input = this._domController.querySelector(`input[data-key=_fv_${dk}_editScheme]`);
        if (input)
            input.value = scheme;
        this.dom.setEditModeState(true);
        this.reload(null, () => { this.setMode(ViewMode.edit); this.updatePanelState(); }, () => { console.error('err edit'); this.dom.applyViewMode(); }, ViewCmdType.viewstartedit);
    }
    /** Сохранение (и опционально закрытие) */
    _saveView(bt, andClose) {
        const autosave = this.element.getAttribute('data-bgsave') === 'on';
        this.modeBusy(bt);
        if (this.mode === ViewMode.insert) {
            this.dom.setEditModeState(false);
            this.reload(null, (data) => {
                this.modeNormal(bt);
                this.setMode(ViewMode.view);
                this.onInsert(data?.newIndex, andClose);
                this.updatePanelState();
            }, () => {
                this.modeNormal(bt);
                console.error('err save');
                this.setMode(ViewMode.insert);
                this.updatePanelState();
            }, ViewCmdType.insertview);
        }
        else if (this.mode === ViewMode.edit) {
            if (!autosave)
                this.dom.setEditModeState(false);
            this.reload(null, () => {
                this.modeNormal(bt);
                if (!autosave)
                    this.setMode(ViewMode.view);
                this.onSave();
                if (andClose)
                    this.close();
                this.updatePanelState();
            }, () => {
                this.modeNormal(bt);
                console.error('err edit');
                this.setMode(ViewMode.edit);
                this.updatePanelState();
            }, ViewCmdType.saveview);
        }
    }
    /** Отмена редактирования */
    _cancelView() {
        this._closeDirty = false;
        this._uncoveredDirty = false;
        this.dom.setEditModeState(false);
        this.reload(null, () => { this.setMode(ViewMode.view); this.updatePanelState(); }, () => { console.error('err cancel'); this.dom.applyViewMode(); }, ViewCmdType.reload);
    }
    onSave() {
        this._closeDirty = false;
        this._uncoveredDirty = false;
        const content = this.element.closest('.im-tabcontent');
        if (content) {
            const panel = content.closest('.im-panel');
            if (panel) {
                TabsPanelWidget.from(panel)?.clearChangeMarker(content.dataset.id ?? "");
            }
        }
        this.element.dispatchEvent(new CustomEvent('onsave'));
    }
    onInsert(newIndex, close) {
        this._closeDirty = false;
        this._uncoveredDirty = false;
        const content = this.element.closest('.im-tabcontent');
        if (content) {
            const panel = content.closest('.im-panel');
            if (panel) {
                TabsPanelWidget.from(panel)?.clearChangeMarker(content.dataset.id ?? "");
            }
        }
        const onInsertAttr = this.element.getAttribute('data-oninsert');
        if (onInsertAttr != null) {
            this.onValueChanged(this.element, onInsertAttr);
        }
        this.element.dispatchEvent(new CustomEvent('oninsert', { detail: { rowID: newIndex, andClose: close } }));
        if (close) {
            this.close();
        }
    }
    onValueChanged(el, cmdListStr) {
        if (cmdListStr == null) {
            cmdListStr = el.getAttribute('data-imonchange') ?? undefined;
        }
        if (cmdListStr !== undefined) {
            this.execCmdString(el, cmdListStr);
        }
        // 2026-08-20: признак несохранённых изменений ставится до таб-специфичной
        // ветки: ниже стоит ранний return для view вне таб-панели, из-за которого
        // факт изменения раньше терялся на обычных страницах.
        // Управляющие поля (onchange: refresh) исключены: они перерисовывают view,
        // а не заполняют форму, и иначе пустая форма считалась бы несохранённой
        // сразу после переключения такого поля.
        // 2026-10-06: скрытые служебные поля таблицы _tpk_* (выбранные и текущая строки, страница,
        // число строк, признак фильтра, группа) — состояние списка, а не данные формы. Иначе выбор
        // строки в любом списке панели вызывал «Закрыть без сохранения?» при переходе к другой панели.
        // Видимые поля строки правки таблицы названы так же (_tpk_<таблица>__<колонка>) — это данные.
        const isTableState = (el.getAttribute('data-key') ?? '').startsWith('_tpk_')
            && el.type === 'hidden';
        if (el.getAttribute('data-onchange') !== 'refresh'
            && !el.hasAttribute('data-no-close-dirty')
            && !isTableState) {
            this._closeDirty = true;
            // 2026-10-15: изменение, которое не сохраняет очередь области фонового сохранения:
            // поле без bgSave и без серверной onchange-команды
            if (el.getAttribute('data-bgsave') !== 'on' && !el.getAttribute('data-onchange')) {
                this._uncoveredDirty = true;
            }
        }
        const content = el.closest('.im-tabcontent');
        if (!content)
            return;
        if (content.dataset.viewchanges === 'True' && this.mode === ViewMode.view) {
            return;
        }
        // NOTE: Перенесено на ImSys
        const panel = content.closest('.im-panel');
        if (panel) {
            TabsPanelWidget
                .from(panel)
                ?.setChangeMarker(content.dataset.id ?? "");
        }
    }
    /**
     *  Клик по кнопке
     * Поддерживаются кнопки с классом im-cmdbutton или data-cmd='ctrl'
     * Если указан data-cmd, то вызов будет обработан через cmdbus
     * иначе вызов пойдет по стандарту imcmd или кнопка будут проигнорирована
     */
    onCtrlClick(bt) {
        if (bt.classList.contains('off'))
            return;
        const map = {
            'saveclose': 'saveClose',
            'delmark': 'delMark',
            'moveup': 'rowsMoveUp',
            'movedown': 'rowsMoveDown'
        };
        const raw = bt.dataset.cmd || '';
        if (!raw) {
            const imcmd = bt.dataset.imcmd;
            if (imcmd)
                this.execCmdString(bt, imcmd);
            return;
        }
        const id = map[raw] || raw;
        const bus = this._cmdBus;
        if (!bus || !bus.has(id)) {
            console.error(`[imView] CmdBus handler missing for "${id}" (from "${raw}")`);
            return;
        }
        bus.emit(id, bt);
    }
    /** Подключаем кнопки формы (save-row / cancel-edit-row) к шине панели */
    _wireInternalFormButtonsToBus() {
        const root = this.element;
        // делегирование на весь view
        on(root, `click.view${this._ui}`, (ev) => {
            const target = ev.target;
            if (!target)
                return;
            const btn = target.closest('button.im-formbutton,[data-type]');
            if (!btn)
                return;
            // кнопки строки таблицы обрабатывает сама таблица (TableWidget._bindEditRowButtons);
            // иначе «Сохранить»/«Отменить» строки запускали сохранение или отмену всего view | 2026-10-06
            const table = btn.closest('.im-table');
            if (table && root.contains(table))
                return;
            const type = btn.getAttribute('data-type') || '';
            if (!type)
                return;
            const map = {
                'save-row': 'save',
                'cancel-edit-row': 'cancel'
                // при необходимости: 'open-form': 'edit'
            };
            const id = map[type];
            if (!id)
                return;
            ev.preventDefault();
            this._cmdBus?.emit(id, btn);
        });
    }
    /** Установить состояние busy (глобальное или по кнопке). */
    modeBusy(btn) {
        //!Warning link-button и btn-unset висят пока остаётся link-button.  btn-unset не переименован // <-- комент до переноса
        this._busyController.modeBusy(btn);
    }
    /** Сбросить состояние busy (глобальное или по кнопке). */
    modeNormal(btn) {
        this._busyController.modeNormal(btn);
    }
    /** Обновить состояние командной панели. */
    updatePanelState() {
        this._busyController.updatePanelState();
    }
    _initEvents() {
        // Общее делегирование событий кликов
        on(this.element, 'click.view', (ev) => {
            const target = ev.target;
            if (!target)
                return;
            // ищем ближайший view
            const ownerView = target.closest('.im-view');
            if (ownerView !== this.element) {
                // событие внутри вложенного view — пропускаем
                return;
            }
            // Проверяем, нажата ли кнопка меню (объединило логику кликов в меню панели, контента, панели таблицы)
            const menuItem = target.closest('.menuitem');
            if (menuItem) {
                const menu = menuItem.closest('.menu');
                if (menu)
                    DomHelpers.hide(menu);
                this.onButtonClick(menuItem);
                const a = menuItem.querySelector('a');
                if (a && a.getAttribute('href') === '#')
                    ev.preventDefault();
            }
            // .im-cmdbutton вне таблиц/тулстрипа — обрабатываем делегированно
            const cmdBtn = target.closest('.im-cmdbutton');
            if (cmdBtn && !cmdBtn.closest('.im-table') && !cmdBtn.closest('.im-toolstrip')) {
                this.onCtrlClick(cmdBtn);
            }
        });
    }
    // --- INIT HTML: public API (вызовите из _create() или из обработчиков server-cmd) ---
    /**
     * Инициализация HTML-контента: навешивает обработчики на найденные элементы.
     */
    initHtml(target, fixAppendView, checkPosition) {
        const t = target ?? this.element;
        // 1) Делегирование инициализации в ближайший внешний .im-view при необходимости
        if (checkPosition) {
            let delegateTo = null;
            if (Array.isArray(t)) {
                const len = t.length;
                if (len === 1 && t[0]?.classList?.contains('im-view')) {
                    delegateTo = t[0].parentElement?.closest('.im-view');
                }
                else if (len > 0) {
                    delegateTo = t[0]?.closest('.im-view');
                }
            }
            else if (t instanceof NodeList) {
                const len = t.length;
                if (len === 1 && t[0]?.classList?.contains('im-view')) {
                    delegateTo = t[0].parentElement?.closest('.im-view');
                }
                else if (len > 0) {
                    delegateTo = t[0]?.closest('.im-view');
                }
            }
            else if (t instanceof HTMLElement) {
                if (t.classList.contains('im-view')) {
                    delegateTo = t.parentElement?.closest('.im-view');
                }
                else {
                    delegateTo = t.closest('.im-view');
                }
            }
            if (delegateTo && delegateTo !== this.element) {
                ViewWidget.getOrInit(delegateTo).initHtml(t, fixAppendView, false);
                return;
            }
        }
        // 2) Готовим список контейнеров для инициализации
        const containers = Array.isArray(t)
            ? t.filter((n) => n instanceof HTMLElement)
            : t instanceof NodeList
                ? Array.from(t)
                : t instanceof HTMLElement
                    ? [t]
                    : [];
        if (containers.length === 0)
            return;
        // 3) Проставляем _x{ui} и инициализируем элементы
        for (const el of containers) {
            el.querySelectorAll('.im-view').forEach(v => v.classList.add(`_x${this._ui}`));
            if (fixAppendView && el.classList.contains('im-view')) {
                el.classList.add(`_x${this._ui}`);
            }
            this._initHtmlElement(el);
        }
    }
    // --- INIT HTML: приватная инициализация одного контейнера ---
    /** Инициализация одного контейнера */
    _initHtmlElement(container) {
        // хелпер: выбираем через this.dom, но оставляем только тех, кто реально под container
        const inContainer = (selector, additionalExclude = '', rootJoin = false) => {
            let root = undefined;
            // Если контейнер является искомым, то внутри не ищем
            if (container.matches?.(selector)) {
                if (!rootJoin)
                    return [container];
                root = container;
            }
            // NOTE Дублируется селерктор not table
            const elems = this.dom.querySelectorAll(selector, ":not(.im-table tbody *)" + additionalExclude).filter(el => container.contains(el));
            if (root)
                elems.push(root);
            return [...new Set(elems)];
        };
        // 1) Validation.init
        Validation.init(container);
        // 2) Кнопки: [data-key], .link-button
        // Один и тот же узел может совпасть с обеими масками, поэтому дедуплицируем.
        // 2026-09-15: пункт меню (.menuitem) обрабатывает делегированный click.view в _initEvents.
        // Если пункту ddMenuItem задать css "link-button", он получит ещё и прямую подписку ниже,
        // и команда пункта уйдёт на сервер дважды. Правильное решение — не вешать класс link-button
        // на пункты меню (исправлено в конфиге Tracking channelNavigationView, кейс 1319).
        const clickButtons = Array.from(new Set([
            ...inContainer('button[data-key]'),
            ...inContainer('.link-button')
        ]));
        clickButtons.forEach(btn => {
            on(btn, `click.view${this._ui}`, (e) => {
                if (!this.onButtonClick(btn))
                    e.preventDefault();
            });
        });
        // 3) Enter-submit: [data-enterbt]
        inContainer('*[data-enterbt]').forEach(ctrl => {
            on(ctrl, `keyup.view${this._ui}`, (ev) => {
                if (ev?.keyCode === 13) {
                    const key = ctrl.getAttribute('data-enterbt') || '';
                    const target = this._domController.querySelector(`#${key}`)
                        || this.dom.itemByKey(key);
                    target?.dispatchEvent(new MouseEvent('click', { bubbles: true }));
                }
            });
        });
        // 5) dropdown: ddchange
        inContainer('.dropdown').forEach(dd => {
            // TODO: Перевести событие на использование el._customChange && !isCustom
            on(dd, `ddchange.view${this._ui}`, () => {
                this.onChangeValue(dd);
                // TODO: Почему тут это вызывается, а в обынчых только onChangeValue
                this.onValueChanged(dd);
            });
        });
        // 6) combo: combochange
        inContainer('.im-combo').forEach(c => {
            // TODO: Перевести событие на использование el._customChange && !isCustom
            on(c, `combochange.view${this._ui}`, () => this.onChangeValue(c));
        });
        // 7) interval-инпуты
        inContainer('input[data-type="interval"]').forEach(inp => {
            if (inp.classList.contains('__i'))
                return;
            inp.classList.add('__i');
            const ms = parseInt(inp.getAttribute('data-interval') || '0', 10);
            if (!Number.isFinite(ms) || ms <= 0)
                return;
            let waiting = false;
            const timer = window.setInterval(() => {
                if (waiting)
                    return;
                waiting = true;
                this.onButtonClick(inp, undefined, () => { waiting = false; }, () => {
                    console.error('interval error');
                    waiting = false;
                });
            }, ms);
            const mo = new MutationObserver(() => {
                if (!document.body.contains(inp)) {
                    clearInterval(timer);
                    mo.disconnect();
                }
            });
            mo.observe(document.body, { childList: true, subtree: true });
        });
        // 8) Общие изменения: [data-key]:not(.im-combo)
        inContainer('[data-key]:not(.im-combo):not(.im-view)', undefined, true).forEach(ctrl => {
            on(ctrl, `change.view${this._ui}`, (e) => {
                const el = ctrl;
                const isCustom = e.detail?.isCustom === true;
                // Возможность обхода стандартных change
                if (el._customChange && !isCustom) {
                    return;
                }
                this.onValueChanged(ctrl);
            });
        });
        // 12) Переключатели видимости: input[data-show]
        inContainer('input[data-show]').forEach(check => {
            check.classList.add('switcher');
            const toggle = () => {
                const mask = check.getAttribute('data-show') || '';
                // ищем по SearchMask с учётом зоны (^...) и @key;
                // sourceEl = сам переключатель — чтобы зона closest работала ожидаемо
                const targets = this.dom.findByMask(mask, check);
                // Показать/скрыть найденные элементы
                targets.forEach(t => {
                    if (check.checked)
                        t.style.removeProperty('display');
                    else
                        t.style.display = 'none';
                });
            };
            on(check, 'change', toggle);
        });
        // 13) Модальные aside
        // 2026-09-14: закрытие панелей подключает только ModalAside через ImSys.initDom ниже,
        // собственная подписка view на крестик убрана, чтобы не было двух владельцев.
        // 14) Сообщаем системе о добавленном html
        ImSys.initDom(container);
        // 15) [data-onchange] / [data-bgsave] (после всего)        
        const extra = Array.from(new Set([
            ...inContainer('*[data-onchange]:not(.im-combo):not(.dropdown):not(.im-view)'),
            ...inContainer('*[data-bgsave]:not(.im-combo):not(.dropdown):not(.im-view)')
        ]));
        extra.forEach(ctrl => on(ctrl, `change.view${this._ui}`, (e) => {
            const el = ctrl;
            const isCustom = e.detail?.isCustom === true;
            // 2026-04-09: Для виджетов вроде AirDatepicker игнорируем служебный native change после reload/init.
            if (el._customChange && !isCustom) {
                return;
            }
            this.onChangeValue(ctrl);
        }));
        // 16) Отложенная подзагрузка .onloading[data-key] (кроме .im-view)
        inContainer('.onloading[data-key]').forEach(it => {
            if (!it.classList.contains('im-view')) {
                this.onDelayLoadingStart(this.element, it);
            }
        });
    }
    /** Ключ элемента View из атрибута data-key */
    get dataKey() {
        return this._options.dataKey;
    }
    /**
     * Возвращает экземпляр ViewWidget для указанного элемента или ближайшего родителя с классом .im-view.
     * @param el Элемент DOM, от которого начинается поиск
     * @returns Экземпляр ViewWidget или undefined, если не найден
     */
    static closestView(el) {
        // TODO: Перейти на поиск data-widget
        let viewEl;
        if (el.classList.contains('im-view')) {
            viewEl = el;
        }
        else {
            viewEl = el.closest(".im-view");
        }
        return viewEl ? ViewWidget.getOrInit(viewEl) : undefined;
    }
    /**
     * Поиск элемента по ключу.
     *
     * Поиск производится с огрнаичением маски uiNotMask
     *
     * @param dataKey Ключ элемента для поиска
     * @param tableMode Устанавливает режим поика таблицы по data-tablekey, иначе поиск производится по data-key
     */
    // TODO Избавиться от метода, в dom controller есть метод
    findElementByDataKey(dataKey, tableMode = false) {
        if (tableMode)
            return this.element.querySelector(`[data-tablekey="${dataKey}"]${this.getUiNotMask()}`) || undefined;
        else
            return this.element.querySelector(`[data-key="${dataKey}"]${this.getUiNotMask()}`) || undefined;
    }
    /**
     * Возвращает родительский ViewWidget (если есть).
     */
    getParentView() {
        // TODO: Перейти на поиск data-widget
        let parent = this.element.parentElement?.closest('.im-view');
        return parent ? ViewWidget.getOrInit(parent) : undefined;
    }
    /**
     * Уничтожение экземпляра класса
     */
    _onDestroy() {
        off(this.element, '.view'); // снять все события в ns "view" (любой тип)
        // NOTE Нет метода снятия по namespace, off('.view' + this._ui);
    }
    /**
    * Создаёт контейнер и инициализирует ViewWidget
    *
    * @param container Родительский элемент, в который будет вставлена view
    * @param options Ключ элемента view - значение для data-key
    * @returns созданный ViewWidget
    */
    static createInside(container, options, position) {
        if (container.querySelector(`[data-key="${options.dataKey}"]`)) {
            throw new Error(`Уже существует view ${options.dataKey}`);
        }
        const html = `<div data-key='${options.dataKey}'></div>`;
        container.insertAdjacentHTML(position ?? "beforeend", html);
        const viewEl = container.querySelector(`[data-key="${options.dataKey}"]`);
        return ViewWidget.getOrInit(viewEl, options);
    }
    /** Подключение UI-команд */
    static _uiCommandProviders() {
        return new ViewUiCommandProvider();
    }
    /** Подключение серверных команд  */
    static _serverCommandProviders() {
        return new ViewServerCmdProvider();
    }
    /**
     * Закрыть текущее view.
     * @param viewOnly   скрыть только содержимое без удаления
     * @param dontAsc    флаг "не подниматься" при закрытии таба
     */
    close(viewOnly = false, dontAsc = false) {
        // 2026-09-14: закрытие панели aside с несохранёнными данными — только после подтверждения.
        // dontAsc = true (как и для вкладок) означает закрытие без вопроса.
        if (!viewOnly && !dontAsc) {
            const asideHost = this.element.closest(".im-modal-aside");
            // 2026-10-15: панель с областью фонового сохранения закрывается после очереди сохранений
            if (asideHost && BgSaveScope.within(asideHost)) {
                requestAsideClose(asideHost);
                return;
            }
            if (asideHost && hasUnsavedAsideChanges(asideHost)) {
                confirmAsideClose(asideHost, () => this.close(viewOnly, true));
                return;
            }
        }
        this.element.style.display = "none";
        if (!viewOnly) {
            const aside = this.element.closest(".im-modal-aside");
            if (aside) {
                aside.remove();
            }
            else {
                this.element.dispatchEvent(new CustomEvent("im-dialog:close", {
                    bubbles: true,
                    composed: true // важно, чтобы событие прошло сквозь Shadow DOM
                }));
            }
            // Сценарий закрытия, когда view в панели закладок           
            const tabContent = this.element.closest(".im-tabcontent");
            if (tabContent) {
                const panelWidget = Widget.closestOnByName(tabContent, TABS_PANEL_WIDGET_KEY);
                if (panelWidget) {
                    panelWidget.closeTab(tabContent.getAttribute("data-id") ?? "", dontAsc !== true);
                }
            }
        }
        this.element.remove();
        // Временная поддержка удаления на стороне старого кода
        ImViewBridge.destroyView(this.element);
        this.destroy();
    }
    /**
     * Выполнение строки UI-команд (совместимость с легаси execCmdString).
     *
     * @param el        целевой элемент (обычно sourceEl)
     * @param cmdString строка команд (parseCmdString)
     * @returns true если команда вернула preventDefault, иначе false
     */
    execCmdString(el, cmdString) {
        try {
            const registry = UiCommandRegistry.uiCommands;
            const runner = new UiCommandRunner(registry);
            const ctx = {
                viewHost: this.element,
                sourceEl: el,
                uiNotMask: this.getUiNotMask(),
            };
            // Снятие экранирования @\someKey -> @someKey
            const escaped = cmdString.replace(/@\\(?=[A-Za-z0-9_{(])/g, "@");
            // Запуск команды
            const results = runner.run(escaped, ctx);
            // нет результатов → false
            if (!results)
                return false;
            // есть preventDefault → true
            if (results.some(r => r?.preventDefault === true))
                return true;
            if (results.some(r => r?.usecmd === true))
                return false;
        }
        catch (err) {
            console.error("[ViewWidget.execCmdString] Ошибка:", err);
        }
        // Возвращаем true, чтобы предотвратить выполнение команды на сервере
        return true;
    }
    /**
     * Выполнить одну серверную команду через ServerCmd-слой.
     *
     * @param cmd       одна команда сервера (ServerCmdData)
     * @param uiNotMask маска исключения; по умолчанию берется из ViewWidget
     * @returns true, если команда была успешно адаптирована и выполнена; иначе false
     */
    execServerCmd(cmd, uiNotMask) {
        return this._executor.execServerCmd(cmd, uiNotMask);
    }
    /**
     * Совместимый с легаси вызов серверной команды.
     * Полностью заменяет старый jQuery-метод doJson(data, callback, onerror, setBusy).
     *
     * @param data      объект запроса к серверу (мы доверяем и сериализуем как есть)
     * @param callback  вызовется при явной команде "callback" ИЛИ один раз в конце,
     *                  если не было ошибок и явных callback-команд
     * @param onerror   вызовется при ошибке или при команде "error"
     * @param setBusy   показывать busy на время запроса (по умолчанию true)
     */
    doJson(data, callback, onerror, setBusy = false) {
        return this._executor.doJson(data, callback, onerror, setBusy);
    }
    /**
     * 2026-10-15: itemcmd элемента внутри области фонового сохранения ([data-bgsave-scope])
     * ставится в последовательную очередь области; вне области — сразу в транспорт.
     * Операция считается ошибочной, если был вызван onerror (в том числе result=error при HTTP 200).
     * @param retryable сохранение значения поля: после ошибки область повторит тот же запрос
     *                  при следующем завершении (BgSaveScope.finish)
     */
    _doItemJson(el, req, callback, onerror, setBusy = false, retryable = false) {
        const scope = BgSaveScope.closest(el);
        if (!scope) {
            this.doJson(req, callback, onerror, setBusy);
            return;
        }
        BgSaveScope.enqueue(scope, this.element, req.itemKey, async () => {
            let failed = false;
            await this.doJson(req, callback, (err) => {
                failed = true;
                onerror?.(err);
            }, setBusy);
            return !failed;
        }, retryable);
    }
    /**
     * Обработка клика по кнопке внутри view.
     * Возвращает false, если нужно предотвратить дефолт, true — если разрешить переход/submit
     *
     * @param btn       Кнопка (элемент с data-key / data-cmd)
     * @param confirmed Флаг "подтверждено" (например после alertConfirm)
     * @param callback  Коллбек при успешном выполнении
     * @param onerror   Коллбек при ошибке
     * @returns true, если действие обработано; false — если отменено
     */
    onButtonClick(el, confirmed, callback, onerror) {
        // Элементы, защищённые от повторного нажатия
        if ((el.classList.contains('cmd-disabled') && el.classList.contains('off')) ||
            el.classList.contains('btn-loading')) {
            return false;
        }
        // Проверка требований/валидности
        if ((!el.getAttribute('data-required') || !el.getAttribute('data-valid'))
            && !this.validation.checkButtonRequires(el)) {
            return false;
        }
        // Проверка капчи (как было)
        if (el.getAttribute('data-capt') != null) {
            const view = el.closest('.im-view');
            const input = view?.querySelector('[data-key="captchaToken"]');
            if (!input || !input.value) {
                view?.querySelector('.captcha')?.classList.add('input-validation-error');
                return false;
            }
            else {
                view?.querySelector('.captcha')?.classList.remove('input-validation-error');
            }
        }
        // Подтверждение
        const confirmText = el.getAttribute('data-confirm');
        if (confirmText && !confirmed) {
            ImSys.alertConfirm(Loc.confirmation, confirmText, () => this.onButtonClick(el, true, callback, onerror));
            return false;
        }
        // Разрешить «нативные» действия
        if (el.getAttribute('data-formaction') != null || el.getAttribute('href') != null) {
            return true;
        }
        // Выполнить строковую imcmd, если задана
        const imcmd = el.getAttribute('data-imcmd');
        if (imcmd) {
            if (imcmd == ViewUiCommand.ctrl) {
                // Поддержка команд ctrl
                this.onCtrlClick(el);
                return false;
            }
            else if (this.execCmdString(el, imcmd)) {
                return false;
            }
        }
        // Команда: либо data-cmd, либо clickitem
        const cmd = el.getAttribute('data-cmd') ?? ViewUiCommand.clickitem;
        // Прерывание выполнения команды
        if (cmd == ViewUiCommand.none)
            return false;
        // Перед серверной сменой языка проверяем несохраненные данные на странице.
        if (el.hasAttribute('data-check-page-close')) {
            const warning = PageCloseController.check();
            if (warning && !window.confirm(warning))
                return false;
        }
        // 2026-10-15: data-wait-bgsave — команда уходит только после успешных фоновых
        // сохранений своей области; при ошибке область разблокируется, команда не отправляется.
        const waitScope = el.hasAttribute('data-wait-bgsave') ? BgSaveScope.closest(el) : null;
        if (waitScope) {
            if (BgSaveScope.isFinishing(waitScope))
                return false;
            this.modeBusy(el);
            BgSaveScope.finish(waitScope).then(ok => {
                if (ok) {
                    this._sendButtonCmd(el, cmd, waitScope, callback, onerror);
                }
                else {
                    this.modeNormal(el);
                }
            });
            return false;
        }
        this.modeBusy(el);
        this._sendButtonCmd(el, cmd, null, callback, onerror);
        return false;
    }
    /**
     * 2026-10-15: команда завершения области для выхода из aside крестиком или Escape
     * (см. ModalAside): та же команда, что у кнопки с data-wait-bgsave, без повторного click.
     * @param btn   кнопка завершения внутри области
     * @param scope область, уже успешно завершённая (BgSaveScope.finish);
     *              разблокируется после ответа сервера
     */
    sendFinishCommand(btn, scope) {
        this.modeBusy(btn);
        this._sendButtonCmd(btn, btn.getAttribute('data-cmd') ?? ViewUiCommand.clickitem, scope);
    }
    /**
     * Отправка серверной команды кнопки (после всех проверок onButtonClick).
     * Индикатор занятости на кнопке уже включён вызывающим кодом.
     * @param finishingScope область, завершённая перед отправкой (data-wait-bgsave):
     *                       разблокируется после ответа; команда идёт мимо очереди области
     */
    _sendButtonCmd(el, cmd, finishingScope, callback, onerror) {
        const busyEl = el;
        // Значения формы
        const values = this.encodeValues();
        // Опции
        const options = {};
        // Если в таблице — добавить tablekey и отмеченные строки
        const table = el.closest('.im-table');
        if (table) {
            options.tablekey = table.getAttribute('data-tablekey') || '';
            options.rowindexes = TableWidgetHelper.checkedRowsIndexes(table);
        }
        // Если кнопка внутри toolstrip — собрать составной ключ
        const toolstripHost = el.closest('.im-toolstrip');
        if (toolstripHost) {
            let fullKey = '';
            let citem = el;
            while (citem && citem.closest('.im-toolstrip')) {
                const dk = citem.getAttribute('data-key');
                if (dk)
                    fullKey = dk + (fullKey ? '.' + fullKey : '');
                citem = citem.parentElement;
            }
            // спец-метки как было
            options.__toolstrip = true;
            options.__item_fullKey = fullKey;
        }
        else {
            // Если кнопка внутри ddmenu — собрать составной ключ и заменить el на сам ddmenu
            const dd = el.closest('.im-ddmenu');
            if (dd) {
                let fullKeyDD = '';
                let citemdd = el;
                while (citemdd && citemdd.closest('.im-ddmenu')) {
                    const dk = citemdd.getAttribute('data-key');
                    if (dk)
                        fullKeyDD = dk + (fullKeyDD ? '.' + fullKeyDD : '');
                    citemdd = citemdd.parentElement;
                }
                el = dd;
                options.__item_fullKey = fullKeyDD;
            }
        }
        // Запрос
        const requestData = {
            type: "itemcmd",
            cmd,
            values,
            options,
            dataKey: this.dataKey,
            itemKey: el.getAttribute('data-key') ?? undefined
        };
        const onDone = (cmdRes) => {
            this.modeNormal(busyEl);
            if (finishingScope)
                BgSaveScope.release(finishingScope);
            if (callback)
                callback(cmdRes);
        };
        const onFail = (cmdErr) => {
            this.modeNormal(busyEl);
            if (finishingScope)
                BgSaveScope.release(finishingScope);
            if (onerror)
                onerror(cmdErr);
        };
        // Вызов: команда завершающей кнопки идёт напрямую, остальные кнопки области — через очередь
        if (finishingScope) {
            this.doJson(requestData, onDone, onFail);
        }
        else {
            this._doItemJson(busyEl, requestData, onDone, onFail);
        }
    }
    /**
     * Универсальный запуск серверной команды (перенос из imView.execCmd).
     */
    execCmd(el, cmdType, cmd, options, callback, onerror, appendValues, setBusy) {
        // Проверка требований, если помечено data-required
        if (el && el.getAttribute('data-required') != null && !this.validation.checkButtonRequires(el)) {
            return false;
        }
        // Значения формы
        const values = this.encodeValues();
        // Опции — объект по умолчанию
        const opts = (options && typeof options === 'object') ? options : {};
        // appendValues перекрывает values (как и было)
        if (appendValues) {
            for (const [k, v] of Object.entries(appendValues)) {
                values[k] = v;
            }
        }
        // itemKey
        let itemKey = el.getAttribute('data-key') ?? el.getAttribute('data-tablekey') ?? undefined;
        // tablekey из ближайшей таблицы, если ещё не задан
        if (!opts.tablekey) {
            const table = el.closest('.im-table');
            if (table) {
                opts.tablekey = table.getAttribute('data-tablekey') || undefined;
            }
        }
        // Поддержка parentKey (inputcollection и т.п.)
        const parentKey = el.getAttribute('data-parentkey');
        if (parentKey != null) {
            opts.parentKey = parentKey;
            if (itemKey == null)
                itemKey = parentKey;
        }
        const requestData = {
            type: cmdType,
            cmd,
            values,
            options: opts,
            dataKey: this.dataKey,
            itemKey
        };
        this.doJson(requestData, callback, onerror, setBusy);
        return true;
    }
    /**
     * Обработчик изменения значения контрола (перенос из легаси onChangeValue).
     * Поддерживает apply-режим (data-applymode), фоновое сохранение (data-bgsave),
     * кастомную команду из data-onchange и busy при data-ocb.
     *
     * @param el       Изменённый элемент (источник)
     * @param cmd      Имя команды; если не задано — берётся из data-onchange / bgsave / onChange
     * @param options  Доп. опции; в apply-режиме дополняются { appliedKeys }
     * @param cmdType  Тип серверной команды (по умолчанию "itemcmd")
     * @param appled   Флаг «применено» для повторного вызова после нажатия Apply
     * @param cb       Коллбек успеха
     * @param er       Коллбек ошибки
     */
    onChangeValue(el, cmd, options, cmdType, appled, cb, er) {
        // 1) валидация: если есть признак ошибки на контроле — не шлём
        if (el.classList.contains('input-validation-error'))
            return;
        // 2) тип команды по умолчанию
        const type = cmdType ?? 'itemcmd';
        // 3) apply-режим: показываем кнопку и копим ключи до подтверждения
        if (el.getAttribute('data-applymode') != null && appled !== true) {
            const dk = el.getAttribute('data-key');
            if (dk) {
                this._appliedKeySet ??= new Set();
                this._appliedKeySet.add(dk);
                this._applyBtn ??= new ApplyButtonController(this.element, { className: "im-bt-apply" });
                // Кнопка показываетcя, а по Apply — отправляем накопленное
                this._applyBtn.showNear(el, () => {
                    const opts = options ? { ...options } : {};
                    if (this._appliedKeySet && this._appliedKeySet.size > 0) {
                        opts.appliedKeys = Array.from(this._appliedKeySet).join(',');
                    }
                    this._appliedKeySet = undefined;
                    this.onChangeValue(el, cmd, opts, type, true, cb, er);
                });
                return; // ждём нажатия Apply
            }
            else {
                console.error('[ViewWidget.onChangeValue] data-applymode requires data-key');
            }
        }
        // 4) определяем команду, если не передана
        let resolvedCmd = cmd;
        if (!resolvedCmd) {
            // a) data-onchange
            resolvedCmd = el.getAttribute('data-onchange') || undefined;
            // b) bgsave=on → команда по умолчанию
            if (!resolvedCmd) {
                if (el.getAttribute('data-bgsave') === 'on') {
                    resolvedCmd = 'itembgsave';
                }
                else {
                    return; // ничего делать не нужно
                }
            }
            // c) «on» / пусто → onchange
            if (resolvedCmd === 'on' || resolvedCmd === '') {
                resolvedCmd = 'onchange';
            }
        }
        // 2026-09-24: команда с перезагрузкой страницы (data-check-page-close) сначала проверяет
        // несохраненные данные; при отказе список возвращается к сохраненному выбору.
        if (el.hasAttribute('data-check-page-close')) {
            const warning = PageCloseController.check();
            if (warning && !window.confirm(warning)) {
                if (el instanceof HTMLSelectElement) {
                    for (const option of Array.from(el.options))
                        option.selected = option.defaultSelected;
                }
                return;
            }
        }
        // 5) busy по требованию (data-ocb)
        const withBusy = el.getAttribute('data-ocb') != null;
        if (withBusy)
            this.modeBusy();
        // 6) собираем payload
        const values = this.encodeValues();
        const opts = options ? { ...options } : {};
        const requestData = {
            type,
            cmd: resolvedCmd,
            values,
            options: opts,
            dataKey: this.dataKey,
            itemKey: el.getAttribute('data-key') ?? undefined
        };
        // пометка фонового сохранения для транспорта
        if (el.getAttribute('data-bgsave') === 'on') {
            requestData.bgSave = true;
        }
        // 7) вызов транспорта; busy прокидываем только если был data-ocb.
        // 2026-10-15: внутри области фонового сохранения — через её очередь;
        // неуспешный bgSave область повторит при завершении (команды добавления не повторяются)
        this._doItemJson(el, requestData, cb, er, withBusy, requestData.bgSave === true);
    }
    /** Отложенная подзагрузка одного элемента внутри view */
    onDelayLoadingStart(view, elem) {
        const uiNotMask = this.getUiNotMask();
        if (!view.querySelector(`[data-key="__form_loaded"]${uiNotMask}`)) {
            const hidden = document.createElement('input');
            hidden.setAttribute('data-key', '__form_loaded');
            hidden.value = 'True';
            hidden.hidden = true;
            view.appendChild(hidden);
            // скелетон на время загрузки
            this.dom.appendSkeleton(elem);
            // подгрузка конкретного блока
            setTimeout(() => {
                const key = elem.getAttribute('data-key') || '';
                this.reload(key, () => {
                    view.querySelectorAll('.skeleton').forEach(s => s.remove());
                    this.modeNormal();
                    // Сообщить системе об обновлённом куске DOM
                    try {
                        ImSys.initDom(elem);
                    }
                    catch { /* ignore */ }
                });
            });
        }
    }
    /** Собирает значения из options.values и элементов формы внутри view. */
    encodeValues() {
        // значения ранее получены из data-values в JSON-объекте или legacy StructuredText
        const base = this.options.values ?? {};
        // выборка элементов: по uiNotMask или с исключением innerexclude
        const mask = this.element.dataset.innerinclude === undefined
            ? `*[data-key]${this.getUiNotMask()}`
            : `:not(.innerexclude [data-key])`;
        // сбор значений с DOM
        const collected = encodeValues(this.element, mask);
        // collected перекрывает base
        return { ...base, ...collected };
    }
    /**
     * Перезагружает содержимое view.
     * @param items Имя элемента (опция items), если нужно перезагрузить часть
     * @param callback Вызывается после успешного ответа
     * @param onerror Вызывается при ошибке
     * @param reloadType Тип операции (по умолчанию "reload")
     * @param appendedValues Дополнительные значения, которые перекрывают encodeValues
     */
    reload(items, callback, onerror, reloadType = ViewCmdType.reload, appendedValues) {
        this.modeBusy();
        const options = {};
        if (items != null && items !== "")
            options.items = items;
        let values = this.encodeValues();
        if (appendedValues)
            values = { ...values, ...appendedValues };
        const req = {
            type: reloadType,
            values,
            options,
            dataKey: this.dataKey,
        };
        this._executor.doJson(req, callback, onerror);
    }
    /**
     * 2026-09-14: есть ли несохранённые изменения, без учёта маркера data-close-control.
     * Используется защитой закрытия modal aside (см. ModalAside.requestAsideClose).
     */
    hasUnsavedChanges() {
        return this._closeDirty;
    }
    /** Есть ли изменения, не покрытые фоновым сохранением */
    hasUncoveredChanges() {
        return this._uncoveredDirty;
    }
    /** Участвует ли view в контроле закрытия страницы */
    get _hasCloseControl() {
        return this.element.matches('[data-close-control]')
            || !!this.element.querySelector('[data-close-control]');
    }
    /**
     * Проверка возможности закрытия страницы (WidgetCloseControl).
     * Вызывается PageCloseController перед закрытием или перезагрузкой.
     */
    canClose() {
        if (!this._closeDirty)
            return null;
        if (!this._hasCloseControl)
            return null;
        return Loc.pageContainsUnsavedData;
    }
    /** Текущий режим view (например "edit", "view") */
    get mode() {
        return this._mode;
    }
    // ВМЕСТО старого jq imView.mode(val)
    /** Изменение/чтение режима (edit/insert/view). */
    setMode(val) {
        switch (val) {
            case ViewMode.edit:
            case ViewMode.insert:
            case ViewMode.view: {
                // установка значения (hidden + поле)
                this._mode = val;
                this.dom.setEditModeState(val !== ViewMode.view);
                break;
            }
            default:
                return;
        }
        // обновление состояния DOM
        this.dom.applyViewMode();
    }
}
/** Имя виджета */
ViewWidget._alias = "View";

export { ViewWidget };
